import { tt } from '@agos/shared'

/**
 * The notification delivery log — FRONTEND.md §9.253, FB-413.
 *
 * `GET /notifications/deliveries` records every attempt and nothing read it, so *did the customer get
 * their SMS* had no answer anywhere in the manager. **64 rows on this data**, every one of them
 * `no_recipient`, newest 2026-09-07.
 *
 * Field names read off the running endpoint, not inferred: `eventKey`, `channel`, `attempt`,
 * `outcome`, `providerResponse`, `latencyMs`, `orderId`, `customerId`, `createdAt`.
 */

export interface DeliveryRow {
  id: number
  eventKey: string
  channel: string
  /** Null on this data for every row — a push that had nobody to go to. */
  recipient: string | null
  attempt: number
  outcome: string
  providerResponse: string | null
  latencyMs: number
  orderId: number | null
  createdAt: string | null
}

export function deliveriesOf(payload: unknown): DeliveryRow[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    if (typeof row.id !== 'number') return []
    return [
      {
        id: row.id,
        eventKey: typeof row.eventKey === 'string' ? row.eventKey : '',
        channel: typeof row.channel === 'string' ? row.channel : '',
        recipient: typeof row.recipient === 'string' && row.recipient !== '' ? row.recipient : null,
        attempt: typeof row.attempt === 'number' ? row.attempt : 1,
        outcome: typeof row.outcome === 'string' ? row.outcome : '',
        providerResponse:
          typeof row.providerResponse === 'string' && row.providerResponse !== ''
            ? row.providerResponse
            : null,
        latencyMs: typeof row.latencyMs === 'number' ? row.latencyMs : 0,
        orderId: typeof row.orderId === 'number' ? row.orderId : null,
        createdAt: typeof row.createdAt === 'string' ? row.createdAt : null,
      },
    ]
  })
}

/**
 * What an outcome means - one tone per thing the dispatcher can say, FRONTEND.md §9.771 (owner O131).
 *
 * **`no_recipient` is its own answer and neither success nor failure.** It is the push channel firing for
 * a customer with no subscription — 114 subscriptions across 2 463 customers is what that looks like
 * in practice. Tinting it as a failure would make a working system look broken; tinting it as a
 * success would hide that nothing arrived.
 *
 * **And so are the three this had folded into the other groups (FB-1090).** `NotificationDispatcher.Outcome`
 * has six values and this had four groups:
 *
 * - `disabled` - the event's switch or the channel's master switch is off. It reached the last line, the one for
 *   an outcome nobody can classify, and read *Zlyhalo* in red, where it is a switch to look at (amber).
 * - `degraded` - three provider failures in a row stood the channel down. It is a fault, so it stays red, and it
 *   says *Pozastavené*, because nothing was tried: the dispatcher steps over a degraded channel for ten minutes
 *   after its latest failure (BACKEND.md §10A.423).
 * - `skipped` - written only for *"earlier channel delivered"*: under *Prvý, ktorý doručí* the push reached the
 *   customer, so the SMS was not needed. Grouped with `no_recipient` it read *Nebolo komu* about a customer who had
 *   just been reached - the opposite of what happened. *Netreba*, muted.
 *
 * An outcome this does not know is still a failure, the safer way round: a delivery nobody can classify is one
 * worth looking at.
 */
export type DeliveryTone = 'sent' | 'failed' | 'disabled' | 'nobody' | 'skipped' | 'pending'

export function outcomeTone(outcome: string): DeliveryTone {
  if (outcome === 'sent' || outcome === 'delivered') return 'sent'
  if (outcome === 'disabled') return 'disabled'
  if (outcome === 'no_recipient') return 'nobody'
  if (outcome === 'skipped') return 'skipped'
  if (outcome === 'retrying' || outcome === 'queued' || outcome === '') return 'pending'
  // `failed`, `degraded`, and anything nobody can classify.
  return 'failed'
}

export function outcomeLabel(outcome: string): string {
  // Red like a failure, and named apart from one: nothing was attempted.
  if (outcome === 'degraded') return tt('Pozastavené')
  const tone = outcomeTone(outcome)
  if (tone === 'sent') return tt('Odoslané')
  if (tone === 'disabled') return tt('Vypnuté')
  if (tone === 'nobody') return tt('Nebolo komu')
  if (tone === 'skipped') return tt('Netreba')
  if (tone === 'pending') return tt('Čaká')
  return tt('Zlyhalo')
}

/**
 * Where a row's *Prečo* came from - the `data-delivery-reason` marker, FRONTEND.md §9.771.
 *
 * - `known` - one of the reasons the dispatcher and the senders write, said in Slovak;
 * - `provider` - anything else, shown exactly as it came: a gateway's own text (`BulkGate: ...`, `Resend: ...`) or
 *   an exception's name is data, not wording, so it never goes through `tt()`;
 * - `unrecorded` - the row carries no reason, so the sentence says that rather than guess one;
 * - `none` - a row that went out, where there is nothing to explain.
 */
export type ReasonSource = 'known' | 'provider' | 'unrecorded' | 'none'

export interface DeliveryReason {
  text: string
  source: ReasonSource
}

/**
 * Why a row ended the way it did (FB-1091): `providerResponse` has been parsed since §9.253 and no column drew it.
 *
 * **Matched exactly**, against the list fixed with O131's backend half - so a provider's sentence that happens to
 * contain a known phrase is still the provider's, and a reason added on the server without a sentence here is shown
 * as it came rather than hidden. A blank reason is no reason.
 */
export function deliveryReason(
  row: Pick<DeliveryRow, 'outcome' | 'channel' | 'providerResponse'>,
): DeliveryReason {
  const stored = row.providerResponse
  if (stored === null || stored.trim() === '') return unrecorded(row.outcome)
  const known = knownReason(stored, row.channel)
  if (known !== null) return { text: known, source: 'known' }
  return { text: stored, source: 'provider' }
}

/** The reasons the server writes, each naming where to fix it when the manager has a place for that. */
function knownReason(reason: string, channel: string): string | null {
  switch (reason) {
    case 'off for this event':
      return tt('Pre túto udalosť je tento kanál vypnutý - zapína sa v Nastavenia → Notifikácie & kanály.')
    case 'channel switched off':
      // No place to name: the channel's master switch is not offered in the manager (§9.253).
      return tt('Kanál je centrálne vypnutý, takže ním neodíde nič.')
    case 'channel degraded':
      /* BACKEND.md §10A.423: the dispatcher steps over a degraded channel for ten minutes from its latest failure, then
         lets one attempt through; a success clears the flag and a failure starts the next ten minutes. */
      return tt('Kanál zlyhal trikrát po sebe, preto sa ním 10 minút nič neposiela - potom sa skúsi znova.')
    case 'earlier channel delivered':
      return tt('Zákazníka už zastihol skorší kanál, takže tento nebol potrebný.')
    case 'no phone number':
      return tt('Objednávka ani zákazník nemá telefónne číslo.')
    case 'no customer e-mail':
      return tt('Objednávka ani zákazník nemá e-mail - push aj e-mail sa posielajú podľa neho.')
    case 'no push subscription':
      return tt('Zákazník nemá push notifikácie povolené v žiadnom zariadení.')
    case 'sms text switched off':
      return tt('Táto SMS je vypnutá - zapína sa v Nastavenia → SMS.')
    case 'sms not configured':
      return tt('SMS brána nie je nastavená - vyplňte Nastavenia → SMS.')
    case 'no wording':
      return wordingMissing(channel)
    // §9.773, BACKEND.md §10A.427 - the two the order e-mails write.
    case 'email not configured':
      return tt('E-mail nie je nastavený - vyplňte odosielateľa a kľúč v Nastavenia → E-mail.')
    case 'order confirmation switched off':
      return tt('Potvrdenie objednávky je vypnuté - zapína sa v Nastavenia → E-mail.')
    default:
      return null
  }
}

/** *No wording* is fixed on the page that holds that channel's texts. */
function wordingMissing(channel: string): string {
  if (channel === 'sms') return tt('Správa nemá text - doplňte ho v Nastavenia → SMS.')
  if (channel === 'email') return tt('Správa nemá text - doplňte ho v Nastavenia → E-mail.')
  if (channel === 'push') return tt('Správa nemá text - doplňte ho v Nastavenia → Pushup.')
  return tt('Správa nemá text, ktorý by sa dal odoslať.')
}

/**
 * A row with no reason - every row older than the senders' reasons, 885 of Šurany's on its dump.
 *
 * **It must not read as if the cause were known**, so each sentence says what the outcome is and that the rest was
 * not recorded. A row that went out needs none: its dash is the absence of a problem, not of a record.
 */
function unrecorded(outcome: string): DeliveryReason {
  const tone = outcomeTone(outcome)
  if (tone === 'sent') return { text: '', source: 'none' }
  if (tone === 'nobody') {
    return { text: tt('Príjemca sa nenašiel - presnejší dôvod sa nezaznamenal.'), source: 'unrecorded' }
  }
  if (tone === 'disabled') {
    return { text: tt('Vypnuté - ktorý vypínač, sa nezaznamenalo.'), source: 'unrecorded' }
  }
  if (tone === 'skipped') return { text: tt('Nepoužil sa - dôvod sa nezaznamenal.'), source: 'unrecorded' }
  if (tone === 'pending') return { text: tt('Pokus ešte nedobehol.'), source: 'unrecorded' }
  return { text: tt('Dôvod sa nezaznamenal.'), source: 'unrecorded' }
}

/** How the log reads at a glance — a count per group, over what came back. */
export interface DeliverySummary {
  total: number
  sent: number
  failed: number
  disabled: number
  nobody: number
  skipped: number
  pending: number
}

export function summariseDeliveries(rows: readonly DeliveryRow[]): DeliverySummary {
  const summary: DeliverySummary = {
    total: rows.length,
    sent: 0,
    failed: 0,
    disabled: 0,
    nobody: 0,
    skipped: 0,
    pending: 0,
  }
  for (const row of rows) summary[outcomeTone(row.outcome)] += 1
  return summary
}

/** One part of the summary line after *pokusov*. */
export interface SummaryPart {
  group: DeliveryTone
  count: number
  word: string
}

/**
 * The summary line's parts, every group the labels have - FRONTEND.md §9.771.
 *
 * **They sum to the total**, because every row lands in exactly one tone. *čakajúcich* is said only when there are
 * any: the dispatcher writes no pending outcome, so a permanent *0 čakajúcich* would be a group that cannot happen,
 * and leaving a non-zero one out is what would break the sum. *zlyhaní* counts `failed` and `degraded` together.
 */
export function summaryParts(summary: DeliverySummary): SummaryPart[] {
  const parts: SummaryPart[] = [
    { group: 'sent', count: summary.sent, word: tt('odoslaných') },
    { group: 'failed', count: summary.failed, word: tt('zlyhaní') },
    { group: 'disabled', count: summary.disabled, word: tt('vypnutých') },
    { group: 'nobody', count: summary.nobody, word: tt('bez príjemcu') },
    { group: 'skipped', count: summary.skipped, word: tt('netreba') },
  ]
  if (summary.pending > 0) {
    parts.push({ group: 'pending', count: summary.pending, word: tt('čakajúcich') })
  }
  return parts
}

/**
 * Said when the log is as long as the limit — `CLAUDE.md`'s no-silent-caps rule.
 *
 * A log showing the first hundred of a thousand and saying nothing reads as *a hundred notifications
 * were sent*, which is a different fact.
 */
export function capNote(rows: readonly DeliveryRow[], limit: number): string {
  return rows.length >= limit
    ? `${tt('Zobrazených je posledných')} ${limit} ${tt('pokusov - starších môže byť viac.')}`
    : ''
}
